import React, { useCallback, useEffect, useState } from "react";
import { UseFormReturn, useFieldArray } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { registerObject } from "go-form";
import useFlash from "go-alert/AlertMessage";
import handleError from "go-app/services/errors";
import { useWindowSize } from "go-core/components/useWindowSize";
import { useListenEvent } from "go-core/hooks";
import { selectOrganization } from "go-security/services/organizations/selectors";
import BarcodeScanner from "../../../../../../../../../../../../components/Product/BarcodeScanner/BarcodeScanner";
import { apiOrganization } from "../../../../../../../../../../../../services/Api/Organization/apiOrganization";
import {
	BundleApi,
	ProductApi,
	ProductStateApi,
} from "../../../../../../../../../../../../services/Api/Organization/types";
import ChangeProductModal from "../../../../../../components/ChangeProductModal";
import BundleFormItemsTableActions from "./BundleFormItemsTableActions";
import BundleFormItemsTableBody from "./BundleFormItemsTableBody";
import BundleFormItemsTableHeader from "./BundleFormItemsTableHeader";

interface Props {
	form: UseFormReturn<BundleApi>;
}

const BundleFormItemsTable = ({ form }: Props) => {
	const { isMobile } = useWindowSize();
	const { watch, control, setValue, register } = form;
	const { t } = useTranslation();
	const watchedItems = watch("items");
	const { fields, remove, append, replace } = useFieldArray({
		control,
		name: "items",
		keyName: "key",
	});
	const { addFlash } = useFlash();
	const [shownIndexes, setShownIndexes] = useState<number[]>([]);
	const [showChangeProductModal, setShowChangeProductModal] = useState<number | null>(null);
	const [shouldFetchDestinationProductStates, setShouldFetchDestinationProductStates] = useState<boolean>(true);
	const [shouldFetchSourceProductStates, setShouldFetchSourceProductStates] = useState<boolean>(true);
	const watchedSourceStockId = watch("source_stock_id");
	const watchedDestinationStockId = watch("destination_stock_id");
	const watchedProductIds = watchedItems.map((item) => item.product_id).filter((id) => id);
	const [destinationProductStates, setDestinationProductStates] = useState<ProductStateApi[]>([]);
	const [sourceProductStates, setSourceProductStates] = useState<ProductStateApi[]>([]);
	const itemsAsString = JSON.stringify(watchedItems);
	const [showBarcodeScanner, setShowBarcodeScanner] = useState<boolean>(false);
	const organization = useSelector(selectOrganization);

	useEffect(() => {
		setShouldFetchDestinationProductStates(false);
		setShouldFetchSourceProductStates(false);
	}, []);

	const checkIfAddNewItem = () => {
		const prevItem = watchedItems[watchedItems?.length - 1];

		return prevItem?.product_id;
	};

	const handleOpenBarcodeScanner = useCallback((event) => {
		if (event.key === "Enter") {
			if (!showBarcodeScanner) setShowBarcodeScanner(true);
		}
	}, []);

	useListenEvent({
		eventName: "keypress",
		callback: (event?: KeyboardEvent) => {
			if (!event) {
				return;
			}
			handleOpenBarcodeScanner(event);
		},
	});

	useEffect(() => {
		if (checkIfAddNewItem()) {
			append({
				position: watchedItems.length - 1,
				product: null,
				product_id: null,
				volume: null,
				quantity: null,
				unit_fifo_cost_net_money: null,
				total_fifo_cost_net_money: null,
				comment: "",
				items: [],
			});
			setShouldFetchDestinationProductStates(false);
		}
	}, [itemsAsString]);

	useEffect(() => {
		setShouldFetchSourceProductStates(true);
	}, [watchedSourceStockId]);
	useEffect(() => {
		setShouldFetchDestinationProductStates(true);
	}, [watchedDestinationStockId]);

	useEffect(() => {
		if (shouldFetchDestinationProductStates) {
			if (watch("destination_stock_id")) {
				fetchProductStates(watch("destination_stock_id"), "destination");
			}
			setShouldFetchDestinationProductStates(false);
		}
	}, [shouldFetchDestinationProductStates]);

	useEffect(() => {
		if (shouldFetchSourceProductStates) {
			if (watch("source_stock_id")) {
				fetchProductStates(watch("source_stock_id"), "source");
			}
			setShouldFetchSourceProductStates(false);
		}
	}, [shouldFetchSourceProductStates]);

	const fetchProductStates = async (stockId: number, type: "source" | "destination") => {
		try {
			const productIds = Array.from(
				new Set([
					...watchedProductIds,
					watchedItems
						.map((watchedItem) => (watchedItem.items || []).map(({ product_id }) => product_id))
						.flat()
						.filter((id) => id !== null),
				])
			);
			const params = {
				include: "volumes",
				stock_id: stockId,
				product_ids: productIds.join(","),
			};
			const res = await apiOrganization.getProductState(params);
			type === "source" ? setSourceProductStates(res) : setDestinationProductStates(res);
		} catch (err) {
			handleError.alert(err, addFlash);
		}
	};

	const searchProducts = async (search: string, params: Record<string, any>, options?: Record<string, any>) => {
		const newParams = {
			...params,
			"productType|e": "COMPOSITE",
		};
		return apiOrganization.getProductSearchSelect(search, newParams, {
			cancelToken: options?.token,
		});
	};

	const handleChangeProduct = async (product: ProductApi, index: number) => {
		if (!product) return;

		setValue(`items.${index}.id`, undefined);
		registerObject(register, `items.${index}.product`, ["measure_type_unit", "name", "id"]);
		setValue(`items.${index}.product`, product);
		setValue(`items.${index}.product_id`, product.id, { shouldDirty: true });

		const productItems = await fetchProductItems(product.id);
		setValue(
			`items.${index}.items`,
			productItems.map((productItem) => ({
				quantity: null,
				product_name: productItem.product_name,
				product_id: productItem.product_id,
				unit_fifo_cost_net_money: productItem.unit_product_cost_money,
				total_fifo_cost_net_money: null,
				state: productItem.state,
				measure_type: productItem.measure_type,
				amount: productItem.amount,
			}))
		);

		setShouldFetchDestinationProductStates(true);
	};

	const fetchProductItems = async (productId: number) => {
		try {
			return apiOrganization.getProductItems(productId);
		} catch (err) {
			handleError.alert(err, addFlash);
			return [];
		}
	};

	const handleConfirmChangeProduct = async (product: ProductApi) => {
		if (showChangeProductModal !== null) {
			await handleChangeProduct(product, showChangeProductModal);
			setShowChangeProductModal(null);
		}
	};

	return (
		<fieldset className="form-group">
			<div className="d-flex align-items-center justify-content-between">
				<h5 className={isMobile ? "mb-0" : ""}>{t("common.word.items")}</h5>
				<BundleFormItemsTableActions
					watchedItems={watchedItems}
					form={form}
					append={append}
					remove={remove}
					replace={replace}
					setShowBarcodeScanner={setShowBarcodeScanner}
					showBarcodeScanner={showBarcodeScanner}
				/>
			</div>
			<div className={`${isMobile ? "table-responsive" : ""}`}>
				<table className={`table table-form positions-table ${isMobile ? "vertical-top" : ""}`}>
					<BundleFormItemsTableHeader
						watchedItems={watchedItems}
						shownIndexes={shownIndexes}
						setShownIndexes={setShownIndexes}
						fields={fields}
					/>
					<BundleFormItemsTableBody
						form={form}
						watchedItems={watchedItems}
						fields={fields}
						searchProducts={searchProducts}
						setShowChangeProductModal={setShowChangeProductModal}
						setShownIndexes={setShownIndexes}
						shownIndexes={shownIndexes}
						handleChangeProduct={handleChangeProduct}
						destinationProductStates={destinationProductStates}
						sourceProductStates={sourceProductStates}
						remove={remove}
						fetchProductItems={fetchProductItems}
						setShouldFetchSourceProductStates={setShouldFetchSourceProductStates}
					/>
				</table>
			</div>
			{showChangeProductModal !== null && (
				<ChangeProductModal
					isShown={showChangeProductModal !== null}
					onHide={() => setShowChangeProductModal(null)}
					onConfirm={handleConfirmChangeProduct}
					onSearchProducts={searchProducts}
					index={showChangeProductModal}
					form={form}
					onParseOptionValue={(option) => option.label}
				/>
			)}
			{showBarcodeScanner && (
				<BarcodeScanner
					isShown={showBarcodeScanner}
					organization={organization}
					onHide={() => setShowBarcodeScanner(false)}
					form={form}
				/>
			)}
		</fieldset>
	);
};

export default BundleFormItemsTable;
